// =====================================================
// SUB-COMPONENTES PARA INFORME AGROPECUARIO MEJORADO
// Crear cada sección como archivo separado, o pegar todo
// al final de InformeAgropecuario.js antes del export
// =====================================================

import React, { useState } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Alert,
  Table,
  Progress,
} from "reactstrap"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ResponsiveContainer,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  LineChart,
  Line,
  Legend,
  ComposedChart,
  Area,
} from "recharts"

const DICTAMEN_THEME = {
  FAVORABLE: {
    bg: "#f0faf4", // verde muy pálido
    border: "#34a853", // verde sobrio
    text: "#1a7431", // verde oscuro legible
    badge: "#d4edda",
    badgeText: "#155724",
    icon: "mdi-check-decagram",
    label: "Favorable",
  },
  CONDICIONAL: {
    bg: "#fef9ee", // ámbar muy pálido
    border: "#f59e0b", // ámbar sobrio
    text: "#92400e", // marrón oscuro
    badge: "#fff3cd",
    badgeText: "#856404",
    icon: "mdi-alert-decagram",
    label: "Condicional",
  },
  DESFAVORABLE: {
    bg: "#fef2f2", // rosa muy pálido (NO rojo saturado)
    border: "#b91c1c", // rojo apagado
    text: "#7f1d1d", // borgoña oscuro
    badge: "#f5d5d5",
    badgeText: "#721c24",
    icon: "mdi-close-octagon",
    label: "Desfavorable",
  },
  // BDSJEA-170: gris, no rojo. No es un rechazo: es que no hay con qué
  // proyectar. Pintarlo del color del rechazo era el problema.
  SIN_DATOS: {
    bg: "#f8fafc",
    border: "#94a3b8",
    text: "#334155",
    badge: "#e2e8f0",
    badgeText: "#334155",
    icon: "mdi-help-circle-outline",
    label: "Sin datos suficientes",
  },
}

// =====================================================
// Formateo (reutilizar o importar de tu archivo)
// =====================================================
const fmtUsd = v =>
  v == null
    ? "—"
    : "USD " + Number(v).toLocaleString("es-AR", { maximumFractionDigits: 0 })
const fmtNum = (v, d = 1) =>
  v == null
    ? "—"
    : Number(v).toLocaleString("es-AR", {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      })
const fmtPct = v => (v == null ? "—" : `${Number(v).toFixed(1)}%`)

// =====================================================
// 1. SECCIÓN DICTAMEN UNIFICADO
// Muestra el semáforo de la proyección crediticia
// =====================================================

export const SeccionDictamen = ({ proyeccion }) => {
  if (!proyeccion || !proyeccion.dictamen) return null

  const d = proyeccion.dictamen
  const r = proyeccion.resumen
  const theme = DICTAMEN_THEME[d.dictamen] || DICTAMEN_THEME.CONDICIONAL

  return (
    <Card className="mb-3 border-0 shadow-sm overflow-hidden">
      <div style={{ height: "4px", background: theme.border }} />
      <CardBody style={{ backgroundColor: theme.bg }}>
        {/* Encabezado */}
        <Row className="align-items-center mb-3">
          <Col>
            <div className="d-flex align-items-center">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center me-3"
                style={{
                  width: 48,
                  height: 48,
                  backgroundColor: theme.badge,
                  border: `2px solid ${theme.border}`,
                }}
              >
                <i
                  className={`mdi ${theme.icon}`}
                  style={{ fontSize: "1.4rem", color: theme.border }}
                />
              </div>
              <div>
                <span
                  style={{
                    fontSize: "0.65rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: theme.text,
                    opacity: 0.6,
                  }}
                >
                  Dictamen de proyección crediticia
                </span>
                <h4
                  className="mb-0"
                  style={{ color: theme.text, fontWeight: 700 }}
                >
                  {theme.label}
                </h4>
              </div>
            </div>
          </Col>
          <Col xs="auto" className="text-end">
            <small
              className="text-muted d-block"
              style={{ fontSize: "0.68rem" }}
            >
              Monto máximo sugerido
            </small>
            <h4 className="mb-0" style={{ color: theme.text, fontWeight: 700 }}>
              {fmtUsd(d.montoMaximoSugerido)}
            </h4>
            {d.plazoRecomendadoMeses && (
              <small className="text-muted">
                {d.plazoRecomendadoMeses} meses ·{" "}
                {d.garantiaSugerida || "sin garantía definida"}
              </small>
            )}
          </Col>
        </Row>

        {/* Recomendación */}
        {d.recomendacion && (
          <p
            style={{
              fontSize: "0.88rem",
              color: theme.text,
              opacity: 0.85,
              marginBottom: "0.75rem",
              lineHeight: 1.4,
            }}
          >
            {d.recomendacion}
          </p>
        )}

        {/* KPIs inline */}
        {r && (
          <div
            className="d-flex flex-wrap gap-3 py-2 px-3 rounded"
            style={{ backgroundColor: "rgba(255,255,255,0.6)" }}
          >
            <KpiMini
              label="Ingreso proyectado"
              value={fmtUsd(r.ingresoTotalUsd)}
            />
            <KpiMini label="Margen neto" value={fmtUsd(r.margenNetoUsd)} />
            <KpiMini
              label="Capacidad pago"
              value={fmtUsd(r.capacidadPagoAnualUsd)}
            />
            <KpiMini
              label="Cuota mensual"
              value={fmtUsd(r.cuotaMaximaMensualUsd)}
            />
          </div>
        )}

        {/* Observaciones + confianza */}
        <div className="mt-2 d-flex flex-wrap gap-1 align-items-center">
          {(d.observaciones || []).map((obs, i) => (
            <span
              key={i}
              className="px-2 py-1 rounded"
              style={{
                fontSize: "0.72rem",
                backgroundColor: `${theme.border}10`,
                color: theme.text,
                border: `1px solid ${theme.border}25`,
              }}
            >
              {obs}
            </span>
          ))}
          {proyeccion.nivelConfianza && (
            <span
              className="px-2 py-1 rounded-pill"
              style={{
                fontSize: "0.68rem",
                backgroundColor: "#e9ecef",
                color: "#495057",
              }}
            >
              Confianza: {proyeccion.nivelConfianza}
            </span>
          )}
        </div>
      </CardBody>
    </Card>
  )
}

const KpiMini = ({ label, value }) => (
  <div style={{ minWidth: "120px" }}>
    <small
      className="text-muted d-block"
      style={{ fontSize: "0.65rem", lineHeight: 1 }}
    >
      {label}
    </small>
    <strong style={{ fontSize: "0.95rem", color: "#1f2937" }}>{value}</strong>
  </div>
)

// =====================================================
// 2. SECCIÓN COMPARATIVO CON ZONA
// Muestra rendimiento del productor vs promedio de la zona
// =====================================================

export const SeccionComparativo = ({ comparativoZona }) => {
  if (!comparativoZona || comparativoZona.length === 0) return null

  return (
    <Card className="mb-3 border-0 shadow-sm text-muted">
      <CardHeader className="bg-transparent py-2">
        <span className="fw-semibold">
          <i className="mdi mdi-chart-bar me-2" />
          Comparativo con promedio de la zona
        </span>
      </CardHeader>
      <CardBody>
        <Table size="sm" hover className="mb-3">
          <thead className="table-light">
            <tr>
              <th>Cultivo</th>
              <th>Zona</th>
              <th className="text-end">Superficie</th>
              <th className="text-end">Rinde productor</th>
              <th className="text-end">Rinde zona</th>
              <th className="text-center">Diferencia</th>
            </tr>
          </thead>
          <tbody>
            {comparativoZona.map((c, i) => {
              const diff = c.diferenciaPct
              const colorDiff =
                diff > 5 ? "success" : diff < -5 ? "danger" : "secondary"
              const iconDiff = diff > 0 ? "↑" : diff < 0 ? "↓" : "="

              return (
                <tr key={i}>
                  <td>
                    <strong>{c.cultivo}</strong>
                  </td>
                  <td>
                    <small className="text-muted">{c.zona || "—"}</small>
                  </td>
                  <td className="text-end">{fmtNum(c.superficie, 0)} ha</td>
                  <td className="text-end">
                    {c.rindeProductor ? (
                      `${fmtNum(c.rindeProductor)} qq/ha`
                    ) : (
                      <small className="text-muted">No declarado</small>
                    )}
                  </td>
                  <td className="text-end">
                    {c.rindeZona ? (
                      <>
                        {`${fmtNum(c.rindeZona)} qq/ha`}
                        {/* BDSJEA-153: el informe declara de dónde salió el rinde
                            de referencia. No es lo mismo una serie oficial que un
                            parámetro cargado a mano, y el ITA se archiva en el
                            legajo de crédito. */}
                        {c.fuenteRindeZona && (
                          <div
                            className="text-muted"
                            style={{ fontSize: "0.7rem" }}
                          >
                            {c.fuenteRindeZona}
                          </div>
                        )}
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="text-center">
                    {diff != null ? (
                      <Badge color={colorDiff} pill>
                        {iconDiff} {fmtPct(Math.abs(diff))}
                      </Badge>
                    ) : (
                      <small className="text-muted">—</small>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>

        {/* Gráfico de barras comparativo */}
        {comparativoZona.some(c => c.rindeZona) && (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart
              data={comparativoZona.filter(c => c.rindeZona)}
              margin={{ top: 5, right: 20, bottom: 5, left: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="cultivo" tick={{ fontSize: 11 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                label={{
                  value: "qq/ha",
                  angle: -90,
                  position: "insideLeft",
                  fontSize: 10,
                }}
              />
              <RTooltip formatter={v => [`${fmtNum(v)} qq/ha`]} />
              <Legend />
              <Bar
                dataKey="rindeProductor"
                name="Productor"
                fill="#198754"
                opacity={0.8}
              />
              <Bar
                dataKey="rindeZona"
                name="Promedio zona"
                fill="#6c757d"
                opacity={0.5}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardBody>
    </Card>
  )
}

// =====================================================
// 3. SECCIÓN SCORING + ALERTAS
// Radar chart con dimensiones del score
// =====================================================

export const SeccionScoring = ({ scoring, alertas }) => {
  if (!scoring) return null

  const scoreTotal = Number(scoring.scoreTotal) || 0
  const clasificacion = scoring.clasificacion || "Sin clasificar"
  const coloresClasif = {
    EXCELENTE: "success",
    BUENO: "info",
    REGULAR: "warning",
    OBSERVADO: "danger",
  }

  // Datos para radar chart - mapear los campos reales de scoring_agropecuario
  const radarData = [
    {
      dimension: "Escala productiva",
      value: Number(scoring.scoreEscalaProductiva) || 0,
    },
    {
      dimension: "Historial productivo",
      value: Number(scoring.scoreHistorialProductivo) || 0,
    },
    {
      dimension: "Condición actual",
      value: Number(scoring.scoreCondicionActual) || 0,
    },
    {
      dimension: "Riesgo climático",
      value: Number(scoring.scoreRiesgoClimatico) || 0,
    },
    {
      dimension: "Cobertura riesgos",
      value: Number(scoring.scoreCoberturaRiesgos) || 0,
    },
    {
      dimension: "Tenencia tierra",
      value: Number(scoring.scoreTenenciaTierra) || 0,
    },
    { dimension: "Márgenes", value: Number(scoring.scoreMargenes) || 0 },
  ].filter(d => d.value > 0) // Solo mostrar dimensiones con valor

  return (
    <Card className="mb-3 border-0 shadow-sm text-muted">
      <CardHeader className="bg-transparent py-2">
        <span className="fw-semibold">
          <i className="mdi mdi-shield-check me-2" />
          Scoring agropecuario
        </span>
      </CardHeader>
      <CardBody>
        <Row>
          <Col md={4} className="text-center">
            {/* Score total */}
            <div className="mb-2">
              <div
                style={{
                  fontSize: "3rem",
                  fontWeight: 700,
                  color:
                    scoreTotal >= 60
                      ? "#198754"
                      : scoreTotal >= 40
                        ? "var(--bs-warning)"
                        : "#dc3545",
                }}
              >
                {scoreTotal}
              </div>
              <small className="text-muted">/ 100</small>
            </div>
            <Badge
              color={coloresClasif[clasificacion] || "secondary"}
              className="px-3 py-2"
              style={{ fontSize: "0.9rem" }}
            >
              {clasificacion}
            </Badge>
            {scoring.recomendacion && (
              <p
                className="text-muted mt-2 mb-0"
                style={{ fontSize: "0.8rem" }}
              >
                {scoring.recomendacion}
              </p>
            )}
          </Col>
          <Col md={8}>
            {/* Radar chart */}
            <ResponsiveContainer width="100%" height={220}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#dee2e6" />
                <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis
                  angle={90}
                  domain={[0, 100]}
                  tick={{ fontSize: 9 }}
                />
                <Radar
                  name="Score"
                  dataKey="value"
                  stroke="#198754"
                  fill="#198754"
                  fillOpacity={0.3}
                  strokeWidth={2}
                />
              </RadarChart>
            </ResponsiveContainer>

            {/* Barras individuales */}
            {radarData.map((d, i) => (
              <div key={i} className="mb-1">
                <div className="d-flex justify-content-between">
                  <small>{d.dimension}</small>
                  <small className="fw-bold">{d.value}</small>
                </div>
                <Progress
                  value={d.value}
                  style={{ height: 6 }}
                  color={
                    d.value >= 60
                      ? "success"
                      : d.value >= 40
                        ? "warning"
                        : "danger"
                  }
                />
              </div>
            ))}
          </Col>
        </Row>

        {/* Alertas */}
        {alertas && alertas.length > 0 && (
          <div className="mt-3 pt-3" style={{ borderTop: "1px solid #dee2e6" }}>
            <h6 className="mb-2">
              <i className="mdi mdi-alert me-1 text-warning" />
              Alertas activas ({alertas.length})
            </h6>
            {alertas.map((a, i) => (
              <Alert
                key={i}
                color={
                  a.severidad === "CRITICA" || a.severidad === "ALTA"
                    ? "danger"
                    : a.severidad === "MEDIA"
                      ? "warning"
                      : "info"
                }
                className="py-1 px-2 mb-1"
              >
                <small>
                  <strong>[{a.tipo || a.categoria}]</strong> {a.mensaje}
                </small>
              </Alert>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  )
}

// =====================================================
// 4. SECCIÓN HISTORIAL DE CAMPAÑAS
// Tabla + gráfico de evolución de rendimientos
// =====================================================

export const SeccionHistorial = ({ historialCampanas }) => {
  if (!historialCampanas || historialCampanas.length === 0) {
    return (
      <Card className="mb-3 border-0 shadow-sm">
        <CardHeader className="bg-transparent py-2">
          <span className="fw-semibold">
            <i className="mdi mdi-history me-2" />
            Historial de campañas
          </span>
        </CardHeader>
        <CardBody>
          <Alert color="light" className="mb-0 text-center">
            No hay datos históricos de campañas anteriores.
          </Alert>
        </CardBody>
      </Card>
    )
  }

  // Agrupar por campaña para el gráfico
  const campanasUnicas = [...new Set(historialCampanas.map(h => h.campana))]
  const chartData = campanasUnicas.map(camp => {
    const items = historialCampanas.filter(h => h.campana === camp)
    const obj = { campana: camp }
    items.forEach(item => {
      obj[item.cultivo + "_rinde"] = item.rindeReal || item.rindeEsperado
      obj[item.cultivo + "_sup"] = item.superficieTotal
    })
    return obj
  })

  // Cultivos para las líneas del gráfico
  const cultivosEnHistorial = [
    ...new Set(historialCampanas.map(h => h.cultivo)),
  ]
  const coloresCultivo = [
    "#4CAF50",
    "#FFC107",
    "#FF9800",
    "#2196F3",
    "#9C27B0",
    "#795548",
  ]

  return (
    <Card className="mb-3 border-0 shadow-sm text-muted">
      <CardHeader className="bg-transparent py-2">
        <span className="fw-semibold">
          <i className="mdi mdi-history me-2" />
          Historial de campañas
        </span>
        <Badge color="secondary" pill className="ms-2">
          {campanasUnicas.length} campañas
        </Badge>
      </CardHeader>
      <CardBody>
        {/* Tabla */}
        <Table size="sm" hover className="mb-3">
          <thead className="table-light">
            <tr>
              <th>Campaña</th>
              <th>Cultivo</th>
              <th className="text-end">Superficie</th>
              <th className="text-end">Rinde esperado</th>
              <th className="text-end">Rinde real</th>
              <th className="text-center">Estado</th>
            </tr>
          </thead>
          <tbody>
            {historialCampanas.map((h, i) => (
              <tr key={i}>
                <td>
                  <Badge color="light" className="text-dark">
                    {h.campana}
                  </Badge>
                </td>
                <td>{h.cultivo}</td>
                <td className="text-end">{fmtNum(h.superficieTotal, 0)} ha</td>
                <td className="text-end">
                  {h.rindeEsperado ? `${fmtNum(h.rindeEsperado)} qq/ha` : "—"}
                </td>
                <td className="text-end">
                  {h.rindeReal ? (
                    <strong>{fmtNum(h.rindeReal)} qq/ha</strong>
                  ) : (
                    <small className="text-muted">Pendiente</small>
                  )}
                </td>
                <td className="text-center">
                  {h.perdidas > 0 ? (
                    <Badge color="danger" pill>
                      Pérdida
                    </Badge>
                  ) : h.cosechadas > 0 ? (
                    <Badge color="success" pill>
                      Cosechado
                    </Badge>
                  ) : (
                    <Badge color="secondary" pill>
                      En curso
                    </Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Gráfico de evolución */}
        {campanasUnicas.length > 1 && (
          <>
            <h6 className="text-muted mb-2" style={{ fontSize: "0.85rem" }}>
              Evolución de rendimientos
            </h6>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart
                data={chartData}
                margin={{ top: 5, right: 20, bottom: 5, left: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="campana" tick={{ fontSize: 10 }} />
                <YAxis
                  tick={{ fontSize: 10 }}
                  label={{
                    value: "qq/ha",
                    angle: -90,
                    position: "insideLeft",
                    fontSize: 10,
                  }}
                />
                <RTooltip formatter={v => [v ? `${fmtNum(v)} qq/ha` : "—"]} />
                <Legend />
                {cultivosEnHistorial.map((cult, i) => (
                  <Line
                    key={cult}
                    type="monotone"
                    dataKey={cult + "_rinde"}
                    name={cult}
                    stroke={coloresCultivo[i % coloresCultivo.length]}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
      </CardBody>
    </Card>
  )
}

// =====================================================
// 5. SECCIÓN TENENCIA DE TIERRA
// Resumen de establecimientos, propio vs arrendado
// =====================================================

export const SeccionTenencia = ({ tenencia, resumenTenencia }) => {
  if (!tenencia || tenencia.length === 0) return null

  const rt = resumenTenencia || {}

  return (
    <Card className="mb-3 border-0 shadow-sm text-muted">
      <CardHeader className="bg-transparent py-2">
        <span className="fw-semibold">
          <i className="mdi mdi-terrain me-2" />
          Tenencia de tierra
        </span>
      </CardHeader>
      <CardBody>
        {/* KPIs de tenencia */}
        <Row className="mb-3">
          <Col md={3}>
            <div className="text-center p-2 bg-light rounded">
              <small className="text-muted d-block">Superficie total</small>
              <h5 className="mb-0">{fmtNum(rt.totalHa, 0)} ha</h5>
            </div>
          </Col>
          <Col md={3}>
            <div className="text-center p-2 bg-light rounded">
              <small className="text-muted d-block">Establecimientos</small>
              <h5 className="mb-0">{rt.cantEstablecimientos || 0}</h5>
            </div>
          </Col>
          <Col md={3}>
            <div className="text-center p-2 bg-light rounded">
              <small className="text-muted d-block">% propio</small>
              <h5
                className="mb-0"
                style={{ color: rt.pctPropio >= 50 ? "#198754" : "var(--bs-warning)" }}
              >
                {rt.pctPropio || 0}%
              </h5>
            </div>
          </Col>
          <Col md={3}>
            <div className="text-center p-2 bg-light rounded">
              <small className="text-muted d-block">Propios / Arrendados</small>
              <h5 className="mb-0">
                {rt.propios || 0} / {rt.arrendados || 0}
              </h5>
            </div>
          </Col>
        </Row>

        {/* Tabla detalle */}
        <Table size="sm" hover className="mb-0">
          <thead className="table-light">
            <tr>
              <th>Establecimiento</th>
              <th>Provincia</th>
              <th>Tenencia</th>
              <th className="text-end">Total (ha)</th>
              <th className="text-end">Agrícola</th>
              <th className="text-end">Ganadera</th>
              <th>Actividad</th>
            </tr>
          </thead>
          <tbody>
            {tenencia.map((t, i) => (
              <tr key={i}>
                <td>
                  <strong>{t.nombre}</strong>
                </td>
                <td>
                  <small>{t.provincia}</small>
                </td>
                <td>
                  <Badge
                    color={
                      t.tipoTenencia?.toLowerCase().includes("propi")
                        ? "success"
                        : "warning"
                    }
                    pill
                  >
                    {t.tipoTenencia}
                  </Badge>
                </td>
                <td className="text-end">{fmtNum(t.superficieTotal, 0)}</td>
                <td className="text-end">
                  {t.superficieAgricola > 0
                    ? fmtNum(t.superficieAgricola, 0)
                    : "—"}
                </td>
                <td className="text-end">
                  {t.superficieGanadera > 0
                    ? fmtNum(t.superficieGanadera, 0)
                    : "—"}
                </td>
                <td>
                  <small className="text-muted">
                    {t.actividadPrincipal || "—"}
                  </small>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Alerta si todo arrendado */}
        {rt.pctPropio === 0 && rt.cantEstablecimientos > 0 && (
          <Alert color="warning" className="mt-2 mb-0 py-1">
            <small>
              <i className="mdi mdi-alert me-1" />
              100% de los establecimientos son arrendados. Mayor riesgo ante no
              renovación de contratos.
            </small>
          </Alert>
        )}
      </CardBody>
    </Card>
  )
}

// =====================================================
// 6. SECCIÓN FLUJO DE CAJA (de la proyección)
// Gráfico de barras con ingresos mensuales proyectados
// =====================================================

export const SeccionFlujoCaja = ({ flujoMensual }) => {
  if (!flujoMensual || flujoMensual.length === 0) return null

  const data = flujoMensual.map(f => ({
    mes: f.mesAnio
      ? f.mesAnio.substring(0, 3) +
        " " +
        (f.mesAnio.split(" ")[1] || "").slice(2)
      : `M${f.mesNumero}`,
    ingreso: f.ingresoEstimadoUsd || 0,
    acumulado: f.acumuladoUsd || 0,
    esCosecha: f.mesCosecha,
  }))

  return (
    <Card className="mb-3 border-0 shadow-sm text-muted">
      <CardHeader className="bg-transparent py-2">
        <span className="fw-semibold">
          <i className="mdi mdi-chart-timeline-variant me-2" />
          Flujo de caja proyectado (12 meses)
        </span>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart
            data={data}
            margin={{ top: 5, right: 20, bottom: 5, left: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
            <YAxis
              tick={{ fontSize: 10 }}
              tickFormatter={v => (v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v)}
            />
            <RTooltip
              formatter={(v, name) => [
                fmtUsd(v),
                name === "ingreso" ? "Ingreso" : "Acumulado",
              ]}
            />
            <Legend />
            <Bar
              dataKey="ingreso"
              name="Ingreso mensual"
              fill="#198754"
              opacity={0.7}
            >
              {data.map((e, i) => (
                <Cell
                  key={i}
                  fill={e.esCosecha ? "#198754" : "#198754"}
                  opacity={e.esCosecha ? 1 : 0.3}
                />
              ))}
            </Bar>
            <Line
              type="monotone"
              dataKey="acumulado"
              name="Acumulado"
              stroke="#0d6efd"
              strokeWidth={2}
              dot={{ r: 3 }}
            />
          </ComposedChart>
        </ResponsiveContainer>

        {/* Meses de cosecha */}
        <div className="mt-2 d-flex flex-wrap gap-1">
          {flujoMensual
            .filter(f => f.mesCosecha)
            .map((f, i) => (
              <Badge key={i} color="success" className="px-2 py-1">
                🌾 {f.mesAnio}: {f.descripcion}
              </Badge>
            ))}
        </div>
      </CardBody>
    </Card>
  )
}
